import React, { useState, useEffect } from 'react';
import { useStore, put, remove, generateId } from '../hooks/useDB';
import PageLoader from '../components/PageLoader';
import { Plus, Trash2, Edit2, X, Calendar, MapPin, Users, PhoneCall, Inbox, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from '../utils/toast';
import { confirm } from '../utils/confirm';

const STATUSES = ['planning', 'confirmed', 'in-progress', 'completed', 'cancelled'];

function Modal({ event, onSave, onClose }) {
  const [form, setForm] = useState(event || {
    name: '', date: '', endDate: '', venue: '', venueMapLink: '',
    description: '', status: 'planning', expectedAttendees: 20,
    primaryPocName: '', primaryPocPhone: '', primaryPocEmail: '', notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const validate = () => {
    if (!form.name.trim()) return 'Event name is required.';
    if (form.date && form.endDate && form.endDate < form.date) return 'End date must be on or after start date.';
    if (form.expectedAttendees && Number(form.expectedAttendees) < 1) return 'Expected attendees must be at least 1.';
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true);
    setError('');
    try {
      await onSave(form);
    } catch (e) {
      setError('Failed to save. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="tw-drawer-backdrop">
      <div className="tw-drawer-panel">
        {/* Header */}
        <div className="p-6 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{event ? 'Edit Meetup Strategy' : 'Create Master Meetup Plan'}</h3>
            <p className="text-xs text-slate-500 mt-1">Setup central date calendars, properties, and core coordinators</p>
          </div>
          <button className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Event / Meetup Name *</label>
            <input 
              value={form.name} 
              onChange={(e) => set('name', e.target.value)} 
              placeholder="e.g. Annual Leadership Summit 2025" 
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Start Date</label>
              <input 
                type="date" 
                value={form.date} 
                onChange={(e) => set('date', e.target.value)} 
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">End Date</label>
              <input 
                type="date" 
                value={form.endDate} 
                onChange={(e) => set('endDate', e.target.value)} 
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Primary Venue / Property</label>
            <input 
              value={form.venue} 
              onChange={(e) => set('venue', e.target.value)} 
              placeholder="e.g. Hilton Shilim Estates, Pune" 
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Venue Map Link / Coordinates</label>
            <input 
              value={form.venueMapLink || ''} 
              onChange={(e) => set('venueMapLink', e.target.value)} 
              placeholder="https://maps.google.com/... or GPS coordinates" 
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Operational Status</label>
              <select 
                value={form.status} 
                onChange={(e) => set('status', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              >
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Expected Attendees</label>
              <input 
                type="number" 
                value={form.expectedAttendees} 
                onChange={(e) => set('expectedAttendees', e.target.value)} 
                min={1} 
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              />
            </div>
          </div>

          <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl space-y-3">
            <span className="text-xs font-bold text-slate-700 uppercase tracking-wider block">Central Coordinator (POC) Coordinates</span>
            <div className="space-y-2">
              <input 
                value={form.primaryPocName} 
                onChange={(e) => set('primaryPocName', e.target.value)} 
                placeholder="POC Name" 
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs focus:border-slate-800 outline-none"
              />
              <div className="grid grid-cols-2 gap-2">
                <input 
                  value={form.primaryPocPhone} 
                  onChange={(e) => set('primaryPocPhone', e.target.value)} 
                  placeholder="POC Handphone" 
                  className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs focus:border-slate-800 outline-none"
                />
                <input 
                  value={form.primaryPocEmail} 
                  onChange={(e) => set('primaryPocEmail', e.target.value)} 
                  placeholder="POC Email Address" 
                  className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs focus:border-slate-800 outline-none"
                />
              </div>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Internal Remarks</label>
            <textarea 
              value={form.notes} 
              onChange={(e) => set('notes', e.target.value)} 
              rows={2} 
              placeholder="Provide strategic context, general requirements, transport arrangements..." 
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-100 bg-white">
          {error && <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
          <div className="flex gap-3 justify-end">
            <button className="px-4 py-2 text-sm font-semibold border border-slate-200 hover:bg-slate-50 rounded-lg transition" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button className="px-4 py-2 text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition flex items-center gap-2 disabled:opacity-60" onClick={handleSave} disabled={saving}>
              {saving && <Loader2 size={14} className="animate-spin" />}
              {event ? 'Commit Changes' : 'Initialize Event'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const statusClass = { 
  planning: 'sh-badge-warning', 
  confirmed: 'sh-badge-success', 
  'in-progress': 'sh-badge-info', 
  completed: 'sh-badge-outline', 
  cancelled: 'sh-badge-danger' 
};

export default function Events({ readOnly = false }) {
  const { data: events, loading } = useStore('events');
  const [modal, setModal] = useState(null);

  const save = async (form) => {
    await put('events', { ...form, id: form.id || generateId() });
    setModal(null);
    window.dispatchEvent(new CustomEvent('planner:data-change'));
    toast.success(form.id ? 'Event updated successfully.' : 'Event created successfully.');
  };

  const del = async (id) => {
    const ok = await confirm('This will permanently erase this meetup plan. Are you sure?', 'Delete Event');
    if (ok) {
      try {
        await remove('events', id);
        window.dispatchEvent(new CustomEvent('planner:data-change'));
        toast.success('Event deleted.');
      } catch {
        toast.error('Failed to delete event.');
      }
    }
  };

  if (loading) return <PageLoader label="Loading events…" sublabel="Fetching the latest meetup plans" />;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold font-display text-slate-900 flex items-center gap-2">
            <Calendar style={{ color: 'var(--accent)' }} size={24} />
            <span>Corporate Meetup Plans</span>
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {readOnly ? 'Confirmed active meetup details' : 'Register and strategy meet plans, offsite details, and executive team leads'}
          </p>
        </div>
        {!readOnly && (
          <button 
            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-semibold transition flex items-center gap-2 self-start sm:self-auto shadow-sm" 
            onClick={() => setModal('new')}
          >
            <Plus size={16} />
            <span>Initialize Meetup Plan</span>
          </button>
        )}
      </div>

      {/* Meetups Roster Grid */}
      {events.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center shadow-sm">
          <Inbox className="mx-auto text-slate-300 mb-3" size={36} />
          <h3 className="text-base font-bold text-slate-800 font-display">No events declared yet</h3>
          <p className="text-sm text-slate-500 mt-1">
            {readOnly ? 'Strategic schedule will appear once drafted by planning coordinators.' : 'Create your initial corporate meetup project record.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {events.map((ev) => (
            <div key={ev.id} className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm hover:border-slate-300 transition space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-100">
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <h3 className="text-lg font-bold text-slate-900 leading-none">{ev.name}</h3>
                    <span className={`sh-badge ${statusClass[ev.status] || 'sh-badge-outline'} capitalize font-semibold tracking-wide `}>
                      {ev.status}
                    </span>
                  </div>
                  {ev.description && <p className="text-xs text-slate-500 leading-relaxed max-w-2xl">{ev.description}</p>}
                </div>

                {!readOnly && (
                  <div className="flex items-center gap-1.5 justify-end flex-wrap">
                    <button 
                      className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-50 border border-slate-150 rounded-lg transition" 
                      onClick={() => setModal(ev)}
                    >
                      <Edit2 size={14} />
                    </button>
                    <button 
                      className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 border border-transparent rounded-lg transition" 
                      onClick={() => del(ev.id)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>

              {/* Stats / Details */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs font-semibold text-slate-600">
                <div className="flex items-center gap-2">
                  <Calendar className="text-slate-400 shrink-0" size={14} />
                  <span>Timeframe: <span className="text-slate-900 font-bold">{ev.date || 'TBD'}</span> {ev.endDate ? `to ${ev.endDate}` : ''}</span>
                </div>
                {ev.venue && (
                  <div className="flex items-center gap-2">
                    <MapPin className="text-slate-400 shrink-0" size={14} />
                    <span>Venue: <span className="text-slate-900 font-bold">{ev.venue}</span></span>
                    {ev.venueMapLink && (
                      <a
                        href={ev.venueMapLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-semibold transition"
                        title="Open in Maps"
                      >
                        <ExternalLink size={12} /> Map
                      </a>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Users className="text-slate-400 shrink-0" size={14} />
                  <span>Roster: <span className="text-slate-900 font-bold">{ev.expectedAttendees} delegates expected</span></span>
                </div>
              </div>

              {/* POC details */}
              {ev.primaryPocName && (
                <div className="pt-3 border-t border-slate-100 flex items-center gap-2 text-xs leading-normal">
                  <PhoneCall className="text-emerald-500 mt-0.5" size={13} />
                  <span className="text-slate-500 font-semibold uppercase tracking-wider text-[10px]">Strategic Coordinator:</span>
                  <span className="text-slate-800 font-bold">{ev.primaryPocName}</span>
                  {ev.primaryPocPhone && <span className="text-slate-400">({ev.primaryPocPhone})</span>}
                  {ev.primaryPocEmail && <span className="text-slate-400 font-mono text-[11px]">— {ev.primaryPocEmail}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {modal && <Modal event={modal === 'new' ? null : modal} onSave={save} onClose={() => setModal(null)} />}
    </div>
  );
}
